import React from 'react';
import merge from 'lodash-es/merge';
import { observer } from 'mobx-react-lite';
import { trace } from 'owa-trace';
import { logUsage } from 'owa-analytics';
import { getCurrentCulture } from 'owa-localize';
import { getUserConfiguration } from 'owa-session-store';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { getSessionId, getClientVersion as getOwaVersion } from 'owa-config';
import { getPhysicalRing } from 'owa-metatags';
import {
    FrameContexts,
    MetaOsAppContainer,
    HubNames,
    FailedReason,
    IMetaOsAppContainerProps,
    IAppManifest,
} from '@metaos/hub-sdk';
import { HostClientType, Themes } from '@metaos/hub-sdk/dist/Services/messages.interface';
import { PageService, AuthenticationService } from 'owa-app-host-services';
import AppHostLoadingState from './AppHostLoadingState';
import AppHostErrorState from './AppHostErrorState';

/*  We need to accept an arbitrary sub-set of the original props to be passed
 *  That's why we need to each object to be cast as Partial<>
 */
type RecursivePartial<T> = T extends object ? DeepPartial<T> : T;
type DeepPartial<T> = Partial<
    {
        [P in keyof T]: T[P] extends object ? RecursivePartial<T[P]> : T[P];
    }
>;

interface AppHostContainerWrapperProps extends DeepPartial<IMetaOsAppContainerProps> {
    appDefinition: IAppManifest;
    contentUrl: string;
    entityId?: string;
    subEntityId?: string;
}

export default observer(function MetaOsAppHostContainerWrapper(
    props: AppHostContainerWrapperProps
): JSX.Element {
    const { appDefinition, contentUrl, entityId, subEntityId, ...propsOverrides } = props;
    const userConfigSettings = getUserConfiguration().SessionSettings;

    const appContext = useComputedValue(() => {
        return {
            user: {
                id: userConfigSettings?.ExternalDirectoryUserGuid || '',
                displayName: userConfigSettings?.UserDisplayName || '',
                userPrincipalName: userConfigSettings?.UserPrincipalName || '',
                tenant: {
                    id: userConfigSettings?.ExternalDirectoryTenantGuid || '',
                },
            },
            app: {
                locale: getCurrentCulture() || '',
                theme: getIsDarkTheme() ? Themes.dark : Themes.default,
                sessionId: getSessionId(),
                host: {
                    name: HubNames.outlook,
                    clientType: HostClientType.web,
                    sessionId: getSessionId(),
                    ringId: getPhysicalRing(),
                    version: getOwaVersion(),
                },
            },
            page: {
                id: entityId ?? appDefinition!.staticTabs![0].entityId,
                subPageId: subEntityId,
                frameContext: FrameContexts.content,
                isMultiWindow: false,
            },
        };
    });

    const sanitizedContentUrl = useComputedValue(() => {
        let url = contentUrl;
        if (contentUrl.indexOf('{upn}') > -1) {
            const replacement = encodeURIComponent(appContext?.user.userPrincipalName || '');
            url = contentUrl.replace(/{upn}/g, replacement);
        }
        if (contentUrl.indexOf('{theme}') > -1) {
            const replacement = encodeURIComponent(appContext?.app?.theme || '');
            url = contentUrl.replace(/{theme}/g, replacement);
        }
        return url;
    }, [contentUrl]);

    const appWrapperProps: IMetaOsAppContainerProps = useComputedValue(() => {
        return {
            hostWindow: window /* Host window*/,
            appDefinition: appDefinition /* App manifest/App definition */,
            contentUrl: sanitizedContentUrl /* App Url to be loaded */,
            context: appContext /* Context passed to hosted app */,
            hubServices: {
                pageService: new PageService(),
                authenticationService: new AuthenticationService(appContext.user),
                hubUiService: {
                    getErrorUi: (reason: FailedReason) => {
                        logUsage('MOS_AppLoadFailure', [reason.toString()]);
                        trace.warn(`App load error: ${reason.toString()}`);
                        return <AppHostErrorState />;
                    },
                    getLoadingUi: () => <AppHostLoadingState />,
                },
            },
        };
    }, [appDefinition, contentUrl]);

    return <MetaOsAppContainer {...merge(appWrapperProps, propsOverrides)} />;
});
